import React from "react";
import { enqueueSnackbar } from "notistack";
import { Modal, Tooltip, Button } from "antd";
import { workflowOneById } from "../../../../libs/apis";
import { WorkflowOneI } from "../../../../libs/interfaces";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import { CloseOutlined, FundViewOutlined } from "@ant-design/icons";
import { OneCard, OneVideo, ProcessStateAlert } from "../../../../libs/shared";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "external" | "internal" | "exit" | "video";
}

interface ShowContainerPropsI {
  id: number;
}

export const ShowContainer = ({ id }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({ open, onClose, id }: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<WorkflowOneI>({
    container: "",
  } as WorkflowOneI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = React.useState<OnePhotoI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as WorkflowOneI);
      setLoading(true);
    };
  }, []);

  const handleGetData = async () => {
    try {
      let dataContainer: WorkflowOneI;
      const { data: dataContainerGet } = await workflowOneById(id);
      if (!dataContainerGet) return;
      dataContainer = dataContainerGet;
      setData(dataContainerGet);

      const lasFotos: string[] = [];
      const arrCards: any[] = [];

      // Fotos del vacío externo
      const externalPhotos = [
        { title: "Foto EIR", src: dataContainer.emptyEirPhoto, alt: "EIR" },
        {
          title: "Foto Documento Inspección Previa",
          src: dataContainer.emptyPreviousInspectionDocumentPhoto,
          alt: "Documento Inspección Previa",
        },
        {
          title: "Foto Placa",
          src: dataContainer.emptyPlatePhoto,
          alt: "Placa",
        },
        {
          title: "Foto Identificación del Conductor",
          src: dataContainer.emptyDriverIdentificationPhoto,
          alt: "Identificación del Conductor",
        },
        {
          title: "Foto Panorámica",
          src: dataContainer.emptyPanoramicPhoto,
          alt: "Panorámica",
          comment: dataContainer.emptyPanoramicComment,
        },
        {
          title: "Foto Sello Adicional",
          src: dataContainer.emptyAditionalStampPhoto,
          alt: "Sello Adicional",
          comment: dataContainer.emptyAditionalStampComment,
        },
        {
          title: "Foto Sello Naviera",
          src: dataContainer.emptyStampNavieraPhoto,
          alt: "Sello Naviera",
          comment: dataContainer.emptyStampNavieraComment,
        },
        {
          title: "Foto Otro Sello",
          src: dataContainer.emptyOtherStampPhoto,
          alt: "Otro Sello",
          comment: dataContainer.emptyOtherStampComment,
        },
        {
          title: "Foto Candado Satelital",
          src: dataContainer.emptySatelliteLockPhoto,
          alt: "Candado Satelital",
          comment: dataContainer.emptySatelliteLockComment,
        },
        {
          title: "Foto Sello Candado Gps",
          src: dataContainer.emptySatelliteLockStampPhoto,
          alt: "Sello Candado Satelital",
          comment: dataContainer.emptySatelliteLockStampComment,
        },
      ];

      externalPhotos.push(
        {
          title: "Foto Lado Derecho",
          src: dataContainer.emptySideRightPhoto,
          alt: "Lado Derecho",
          comment: "",
        },
        {
          title: "Foto Lado Izquierdo",
          src: dataContainer.emptySideLeftPhoto,
          alt: "Lado Izquierdo",
          comment: "",
        },
        {
          title: "Foto Techo Interno",
          src: dataContainer.emptySideUpPhoto,
          alt: "Techo Interno",
          comment: "",
        },
        {
          title: "Foto Piso Interno",
          src: dataContainer.emptySideDownPhoto,
          alt: "Piso Interno",
          comment: "",
        },
        {
          title: "Foto Frontal",
          src: dataContainer.emptyFrontPhoto,
          alt: "Frontal",
          comment: "",
        },
        {
          title: "Foto Posterior",
          src: dataContainer.emptyRearPhoto,
          alt: "Frontal",
          comment: "",
        },
      );

      externalPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "external",
        });
        lasFotos.push(photo.src);
      });

      // Fotos del vacío interno
      type InternalPhotoKeys = `emptyInternalPhoto${1 | 2 | 3 | 4 | 5 | 6}`;
      type InternalCommentKeys = `emptyInternalComment${1 | 2 | 3 | 4 | 5 | 6}`;

      const internalPhotos = [
        {
          title: "Foto Piso",
          src: dataContainer.emptyFloorPhoto,
          alt: "Piso",
          comment: dataContainer.emptyFloorComment,
        },
        {
          title: "Foto Techo",
          src: dataContainer.emptyRoofPhoto,
          alt: "Techo",
          comment: dataContainer.emptyRoofComment,
        },
        {
          title: "Foto Cubierta del Espejo",
          src: dataContainer.emptyMirrorCoverPhoto,
          alt: "Cubierta del Espejo",
          comment: dataContainer.emptyMirrorCoverComment,
        },
      ];

      for (let i = 1; i <= 6; i++) {
        const photoKey = `emptyInternalPhoto${i}` as InternalPhotoKeys;
        const commentKey = `emptyInternalComment${i}` as InternalCommentKeys;

        internalPhotos.push({
          title: `Foto Interna ${i}`,
          src: dataContainer[photoKey],
          alt: `Interna ${i}`,
          comment: dataContainer[commentKey],
        });
        lasFotos.push(dataContainer[photoKey]);
      }
      internalPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "internal",
        });
        lasFotos.push(photo.src);
      });

      // Fotos de salida
      const exitPhotos = [
        {
          title: "Foto Panorámica",
          src: dataContainer.exitPanoramicPhoto,
          alt: "Panorámica",
          comment: dataContainer.exitPanoramicComment,
        },
        {
          title: "Foto Sello Naviera",
          src: dataContainer.exitStampNavieraPhoto,
          alt: "Sello Naviera",
          comment: dataContainer.exitStampNavieraComment,
        },
        {
          title: "Foto Otro Sello",
          src: dataContainer.exitOtherStampPhoto,
          alt: "Otro Sello",
          comment: dataContainer.exitOtherStampComment,
        },
        {
          title: "Foto Sello Candado Satelital",
          src: dataContainer.exitSatelliteLockStampPhoto,
          alt: "Candado Satelital",
          comment: dataContainer.exitSatelliteLockStampComment,
        },
        {
          title: "Foto Maquinaria 1",
          src: dataContainer.exitEngineryPhoto1,
          alt: "Maquinaria 1",
          comment: dataContainer.exitEngineryComment1,
        },
        {
          title: "Foto Maquinaria 2",
          src: dataContainer.exitEngineryPhoto2,
          alt: "Maquinaria 2",
          comment: dataContainer.exitEngineryComment2,
        },
      ];

      exitPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "exit",
        });
        lasFotos.push(photo.src);
      });

      // Fotos extra agregadas por el usuario (nombre = comment)
      (dataContainer.images || [])
        .filter((img) => img.src && img.section)
        .forEach((img) => {
          const name = (img.comment || "").trim() || "Foto extra";
          arrCards.push({
            title: name,
            noLine: true,
            photos: [{ src: img.src, alt: name, showAlt: false }],
            value: "",
            type:
              img.section === "interior"
                ? "internal"
                : img.section === "exit"
                  ? "exit"
                  : "external",
          });
          lasFotos.push(img.src);
        });

      // Videos adicionales
      const videos = [
        {
          src: dataContainer.emptyInternalVideo,
          alt: "Video Interno",
          type: "video",
        },
        {
          src: dataContainer.exitDoorVideo,
          alt: "Video Puerta",
          type: "video",
        },
        {
          src: dataContainer.exitEngineryVideo,
          alt: "Video Maquinaria",
          type: "video",
        },
      ];

      setTotalPhotos(lasFotos);
      setCardVideos(videos);
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <p
          style={{
            color: "white",
            padding: "10px",
            fontWeight: "bold",
            fontSize: "1.2rem",
            textAlign: "center",
            background: "linear-gradient(to bottom, #FFA500, #FF4500)", // Naranja a rojo
            borderRadius: "5px",
            width: "98%",
            margin: "0 auto",
          }}
        >
          {`CONTENEDOR N° ${data.container}`}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={810}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      style={{ padding: 0 }}
    >
      <Box sx={{ overflowY: "auto", maxHeight: 550, p: 1 }}>
        <ProcessStateAlert
          state={data.processState}
          reason={data.processStateReason}
        />
        {/* Fotos Externas */}
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            mb: 4,
            bgcolor: "transparent",
            borderRadius: "4px",
            background:
              "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
          }}
        >
          <h3 style={{ color: "white" }}>Fotos Externas</h3>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            justifyContent: "center",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {cards
            .filter((el) => el.type === "external")
            .map((el) => (
              <OneCard
                data={el}
                key={`${el.title}-card`}
                lasFotos={totalPhotos}
              />
            ))}
        </Box>
        {/* Fotos Internas */}
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            mb: 4,
            mt: 8,
            bgcolor: "transparent",
            borderRadius: "4px",
            background:
              "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
          }}
        >
          <h3 style={{ color: "white" }}>Fotos Internas</h3>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            justifyContent: "center",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {cards
            .filter((el) => el.type === "internal")
            .map((el) => (
              <OneCard
                data={el}
                key={`${el.title}-card`}
                lasFotos={totalPhotos}
              />
            ))}
        </Box>
        {/* Fotos Rumbo a finca */}
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            mb: 4,
            mt: 8,
            bgcolor: "transparent",
            borderRadius: "4px",
            background:
              "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
          }}
        >
          <h3 style={{ color: "white" }}>Fotos Rumbo a finca</h3>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            justifyContent: "center",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {cards
            .filter((el) => el.type === "exit")
            .map((el) => (
              <OneCard
                data={el}
                key={`${el.title}-card`}
                lasFotos={totalPhotos}
              />
            ))}
        </Box>
        {/* Videos */}
        {/* <Box
          sx={{
            width: "100%",
            textAlign: "center",
            mb: 2,
            mt: 8,
            bgcolor: "transparent",
            borderRadius: "4px",
            background:
              "linear-gradient(to bottom, #A9A9A9 0%,#032263 50%,rgb(29, 68, 152) 100%)",
          }}
        >
          <h3 style={{ color: "white" }}>Videos</h3>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            justifyContent: "center",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {cardVideos.map((el) => (
            <OneVideo {...el} key={`${el.alt}-video`} />
          ))}
        </Box> */}
      </Box>
    </Modal>
  );
};
